import { useNavigate, useParams, useSearchParams } from '@ksyos/router';
import { useEffect, useState } from 'react';
import type { IEmbloomQuestionnaire } from '../../interfaces/embloomQuestionnaire';
import { embloomQuestionnaireServiceOrganizationSettingSchema } from '../../../../common/validation/embloomQuestionnaireServiceOrganizationSetting';
import { fetchJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import { validate } from '../../../utils/validation';
import { Button, ButtonContainer, FormContainer, Header, SaveButton, ThirdHeader } from '../../elements/common';
import { FixedWidthTemplate } from '../../templates/FixedWidthTemplate';
import { EmbloomQuestionnaireServiceOrganizationSettingTable } from './EmbloomQuestionnaireServiceOrganizationSettingTable';

interface IServiceOrganizationQuestionnaires {
    embloomQuestionnaire: {
        id: string;
    };
}

export function EmbloomQuestionnaireServiceOrganizationSettingAddEdit() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const [questionnaires, setQuestionnaires] = useState<IEmbloomQuestionnaire[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [embloomQuestionnaireSelection, setEmbloomQuestionnaireSelection] = useState<string[]>([]);

    useEffect(() => {
        loadQuestionnaires(id);
    }, [id]);

    return (
        <FixedWidthTemplate
            breadcrumb={[
                { text: translateKey('serviceProvider.serviceOrganizationSettings'), url: returnTo() },
                { text: translateKey('embloomQuestionnaires.name') },
            ]}
        >
            <Header content={translateKey('embloomQuestionnaires.name')} />
            <FormContainer onSubmit={async () => null}>
                <ThirdHeader text={translateKey('embloomQuestionnaires.isDecisionSupport')} />

                <EmbloomQuestionnaireServiceOrganizationSettingTable
                    changeSelection={changeSelection}
                    embloomQuestionnaireSelection={embloomQuestionnaireSelection}
                    questionnaires={getDecisionSupport()}
                />

                <ThirdHeader text={translateKey('embloomQuestionnaires.otherQuestionnaires')} />

                <EmbloomQuestionnaireServiceOrganizationSettingTable
                    changeSelection={changeSelection}
                    embloomQuestionnaireSelection={embloomQuestionnaireSelection}
                    questionnaires={getOther()}
                />

                <ButtonContainer
                    alignRight
                    buttons={[
                        <Button
                            color="secondary"
                            disabled={isSaving}
                            onClick={() => setStandardSet()}
                            outlined
                        >
                            {translateKey('embloomQuestionnaires.setBasicSet')}
                        </Button>,
                        <SaveButton
                            disabled={isSaving}
                            onClick={() => save()}
                        />,
                    ]}
                />
            </FormContainer>
        </FixedWidthTemplate>
    );

    function changeSelection(checked: boolean, selectionId: string) {
        const selected = [...embloomQuestionnaireSelection];
        if (checked) {
            selected.push(selectionId);
        } else {
            selected.splice(selected.indexOf(selectionId), 1);
        }
        setEmbloomQuestionnaireSelection(selected);
    }

    async function loadQuestionnaires(serviceOrganizationSettingId: string | undefined) {
        if (serviceOrganizationSettingId) {
            const resultServiceOrganizationQuestionnaires = await fetchJson(
                `/api/serviceServiceOrganizationSettings/${serviceOrganizationSettingId}/embloomQuestionnaireServiceOrganizationSettings`,
                'GET'
            );
            const selected = resultServiceOrganizationQuestionnaires.json.items.map(
                (item: IServiceOrganizationQuestionnaires) => item.embloomQuestionnaire.id
            );
            setEmbloomQuestionnaireSelection(selected);
        }

        const resultQuestionnaires = await fetchJson('/api/embloomQuestionnaires', 'GET');
        setQuestionnaires(resultQuestionnaires.json.items);
    }

    async function save() {
        setIsSaving(true);

        const { errors, item } = validate(embloomQuestionnaireServiceOrganizationSettingSchema, {
            embloomQuestionnaireSelection,
        });
        if (errors.size > 0) {
            throw new Error('Error in validating embloomQuestionnaireSelection selection');
        }

        await fetchJson(
            `/api/serviceServiceOrganizationSettings/${id}/embloomQuestionnaireServiceOrganizationSettings`,
            'POST',
            item
        );

        navigate(returnTo());
    }

    function getDecisionSupport() {
        return questionnaires.filter((item) => item.isDecisionSupport && item.isActive);
    }

    function getOther() {
        return questionnaires.filter((item) => !item.isDecisionSupport && item.isActive);
    }

    function setStandardSet() {
        setEmbloomQuestionnaireSelection(
            questionnaires.filter((item) => item.isStandard && item.isActive).map((item) => item.id)
        );
    }

    function returnTo() {
        const organizationId = searchParams.get('organizationId');
        const serviceId = searchParams.get('serviceId');
        const params = new URLSearchParams({
            ...(organizationId ? { organizationId } : {}),
            ...(serviceId ? { serviceId } : {}),
        });

        return `/serviceOrganizationSettings/${id}?${params.toString()}`;
    }
}
